import { Link } from "wouter";
import {
  Rocket, TrendingUp, Award, FileText, Users, Briefcase,
  Calendar, MessageSquare, Plus, UserPlus, Handshake
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { RoleBasedNextSteps } from "@/components/whatsnext";
import type { User as UserType } from "@shared/schema";

interface RoleTabProps {
  role: "founder" | "investor" | "executive" | "contributor" | "advisor";
  user: UserType;
  dashboardStats: any;
}

export function RoleTab({ role, user, dashboardStats }: RoleTabProps) {
  return (
    <div className="space-y-8">
      <section data-testid={`section-${role}-nextsteps`}>
        <RoleBasedNextSteps user={user} approvedRoles={[role]} singleRoleContext />
      </section>

      {role === "founder" && <FounderContent user={user} dashboardStats={dashboardStats} />}
      {role === "investor" && <InvestorContent user={user} dashboardStats={dashboardStats} />}
      {role === "executive" && <ExecutiveContent user={user} dashboardStats={dashboardStats} />}
      {role === "contributor" && <ContributorContent user={user} dashboardStats={dashboardStats} />}
      {role === "advisor" && <AdvisorContent user={user} dashboardStats={dashboardStats} />}
    </div>
  );
}

function FounderContent({ user, dashboardStats }: { user: UserType; dashboardStats: any }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <div className="h-12 w-12 rounded-full bg-green-500/10 dark:bg-green-400/10 flex items-center justify-center">
          <Rocket className="h-6 w-6 text-green-600 dark:text-green-400" />
        </div>
        <div>
          <h2 className="font-heading text-xl font-bold text-foreground">Founder Hub</h2>
          <p className="text-sm text-muted-foreground">
            {user.founderCompanyName || "Set up your company profile to get started"}
          </p>
        </div>
      </div>

      {user.founderStage && (
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="secondary" className="no-default-active-elevate capitalize">{user.founderStage}</Badge>
          {user.founderFundingStatus && (
            <Badge variant="outline" className="no-default-active-elevate capitalize">{user.founderFundingStatus}</Badge>
          )}
          {user.founderGeography && (
            <Badge variant="outline" className="no-default-active-elevate capitalize">{user.founderGeography}</Badge>
          )}
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card className="hover-elevate" data-testid="card-founder-capital">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-3">
              <TrendingUp className="h-5 w-5 text-green-600 dark:text-green-400" />
              <h3 className="font-semibold text-foreground">Capital & Fundraising</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Access investor directory, pitch tools, and deal readiness checklist</p>
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" variant="outline" asChild data-testid="button-navigate-capital">
                <Link href="/capital">
                  <TrendingUp className="mr-1 h-3 w-3" />
                  Capital Hub
                </Link>
              </Button>
              <Button size="sm" variant="outline" asChild data-testid="button-navigate-deal-readiness">
                <Link href="/capital/deal-readiness">
                  <Briefcase className="mr-1 h-3 w-3" />
                  Deal Readiness
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="hover-elevate" data-testid="card-founder-directory">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-3">
              <Users className="h-5 w-5 text-green-600 dark:text-green-400" />
              <h3 className="font-semibold text-foreground">Founder Directory</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Connect with other founders, share insights, and collaborate</p>
            <Button size="sm" variant="outline" asChild data-testid="button-navigate-founders">
              <Link href="/directory?tab=founders">
                <Users className="mr-1 h-3 w-3" />
                Browse Founders
              </Link>
            </Button>
          </CardContent>
        </Card>

        <Card className="hover-elevate" data-testid="card-founder-community">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-3">
              <MessageSquare className="h-5 w-5 text-green-600 dark:text-green-400" />
              <h3 className="font-semibold text-foreground">Community</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Post asks, book office hours, and get guidance from advisors</p>
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" variant="outline" asChild data-testid="button-navigate-ask-board">
                <Link href="/community/ask-board">
                  <MessageSquare className="mr-1 h-3 w-3" />
                  Ask Board
                </Link>
              </Button>
              <Button size="sm" variant="outline" asChild data-testid="button-navigate-office-hours">
                <Link href="/network/office-hours">
                  <Calendar className="mr-1 h-3 w-3" />
                  Office Hours
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function InvestorContent({ user, dashboardStats }: { user: UserType; dashboardStats: any }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <div className="h-12 w-12 rounded-full bg-blue-500/10 dark:bg-blue-400/10 flex items-center justify-center">
          <TrendingUp className="h-6 w-6 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h2 className="font-heading text-xl font-bold text-foreground">Investor Hub</h2>
          <p className="text-sm text-muted-foreground">Deal flow & portfolio management</p>
        </div>
      </div>

      {(dashboardStats?.pendingIntros ?? 0) > 0 && (
        <Card className="border-blue-200 dark:border-blue-800" data-testid="card-investor-intros-alert">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <UserPlus className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                <div>
                  <p className="font-medium text-foreground">{dashboardStats.pendingIntros} pending introductions</p>
                  <p className="text-sm text-muted-foreground">Founders are requesting to connect with you</p>
                </div>
              </div>
              <Button size="sm" asChild data-testid="button-navigate-review-intros">
                <Link href="/intro-preferences">Review Intros</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card className="hover-elevate" data-testid="card-investor-dealflow">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-3">
              <Briefcase className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <h3 className="font-semibold text-foreground">Deal Flow</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Review curated deals and manage your investment pipeline</p>
            <Button size="sm" variant="outline" asChild data-testid="button-navigate-deal-flow">
              <Link href="/investor-network">
                <Briefcase className="mr-1 h-3 w-3" />
                View Deal Flow
              </Link>
            </Button>
          </CardContent>
        </Card>

        <Card className="hover-elevate" data-testid="card-investor-founders">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-3">
              <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <h3 className="font-semibold text-foreground">Founder Pipeline</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Browse the founder directory and discover opportunities</p>
            <Button size="sm" variant="outline" asChild data-testid="button-navigate-founders-investor">
              <Link href="/directory?tab=investors">
                <Users className="mr-1 h-3 w-3" />
                Browse Founders
              </Link>
            </Button>
          </CardContent>
        </Card>

        <Card className="hover-elevate" data-testid="card-investor-clubs">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-3">
              <Award className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <h3 className="font-semibold text-foreground">Investor Clubs</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Join clubs to co-invest and share due diligence</p>
            <Button size="sm" variant="outline" asChild data-testid="button-navigate-view-clubs">
              <Link href="/investor-network">
                <Award className="mr-1 h-3 w-3" />
                View Clubs
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ExecutiveContent({ user, dashboardStats }: { user: UserType; dashboardStats: any }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <div className="h-12 w-12 rounded-full bg-purple-500/10 dark:bg-purple-400/10 flex items-center justify-center">
          <Award className="h-6 w-6 text-purple-600 dark:text-purple-400" />
        </div>
        <div>
          <h2 className="font-heading text-xl font-bold text-foreground">Executive Hub</h2>
          <p className="text-sm text-muted-foreground">Board opportunities & leadership community</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card className="hover-elevate" data-testid="card-exec-boards">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-3">
              <Award className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              <h3 className="font-semibold text-foreground">Board Seats</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Browse available board positions and governance opportunities</p>
            <Button size="sm" variant="outline" asChild data-testid="button-navigate-board-seats">
              <Link href="/board-opportunities">
                <Award className="mr-1 h-3 w-3" />
                View Board Seats
              </Link>
            </Button>
          </CardContent>
        </Card>

        <Card className="hover-elevate" data-testid="card-exec-circles">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-3">
              <Users className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              <h3 className="font-semibold text-foreground">Peer Circles</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Join executive peer mentorship circles for focused support</p>
            <Button size="sm" variant="outline" asChild data-testid="button-navigate-my-circles">
              <Link href="/circles">
                <Users className="mr-1 h-3 w-3" />
                My Circles
              </Link>
            </Button>
          </CardContent>
        </Card>

        <Card className="hover-elevate" data-testid="card-exec-dinners">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-3">
              <Calendar className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              <h3 className="font-semibold text-foreground">Executive Forums</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Exclusive executive forum series for networking</p>
            <Button size="sm" variant="outline" asChild data-testid="button-navigate-view-events">
              <Link href="/events">
                <Calendar className="mr-1 h-3 w-3" />
                View Events
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ContributorContent({ user, dashboardStats }: { user: UserType; dashboardStats: any }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <div className="h-12 w-12 rounded-full bg-orange-500/10 dark:bg-orange-400/10 flex items-center justify-center">
          <FileText className="h-6 w-6 text-orange-600 dark:text-orange-400" />
        </div>
        <div>
          <h2 className="font-heading text-xl font-bold text-foreground">Contributor Hub</h2>
          <p className="text-sm text-muted-foreground">Content publishing & editorial pipeline</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card className="hover-elevate" data-testid="card-contrib-articles">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-3">
              <FileText className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              <h3 className="font-semibold text-foreground">My Articles</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">View your drafts, published articles, and submission status</p>
            <Button size="sm" variant="outline" asChild data-testid="button-navigate-contributor-dashboard">
              <Link href="/contributor">
                <FileText className="mr-1 h-3 w-3" />
                Dashboard
              </Link>
            </Button>
          </CardContent>
        </Card>

        <Card className="hover-elevate" data-testid="card-contrib-new">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-3">
              <Plus className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              <h3 className="font-semibold text-foreground">New Article</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Start a new article or submit a pitch to the editorial team</p>
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" variant="outline" asChild data-testid="button-navigate-new-article">
                <Link href="/contributor/pitch">
                  <Plus className="mr-1 h-3 w-3" />
                  New Article
                </Link>
              </Button>
              <Button size="sm" variant="outline" asChild data-testid="button-navigate-submit-content">
                <Link href="/content/submit">
                  <FileText className="mr-1 h-3 w-3" />
                  Submit Content
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="hover-elevate" data-testid="card-contrib-review">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-3">
              <Award className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              <h3 className="font-semibold text-foreground">Content Review</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Review community submissions and vote on content</p>
            <Button size="sm" variant="outline" asChild data-testid="button-navigate-review-queue">
              <Link href="/content/peer-review">
                <Award className="mr-1 h-3 w-3" />
                Review Queue
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function AdvisorContent({ user, dashboardStats }: { user: UserType; dashboardStats: any }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <div className="h-12 w-12 rounded-full bg-teal-500/10 dark:bg-teal-400/10 flex items-center justify-center">
          <Handshake className="h-6 w-6 text-teal-600 dark:text-teal-400" />
        </div>
        <div>
          <h2 className="font-heading text-xl font-bold text-foreground">Advisor Hub</h2>
          <p className="text-sm text-muted-foreground">Board advisory, mentorship & peer circles</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card className="hover-elevate" data-testid="card-advisor-boards">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-3">
              <Award className="h-5 w-5 text-teal-600 dark:text-teal-400" />
              <h3 className="font-semibold text-foreground">Board Advisory</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Find board advisory opportunities and governance roles</p>
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" variant="outline" asChild data-testid="button-navigate-board-match">
                <Link href="/board-match">
                  <Award className="mr-1 h-3 w-3" />
                  Board Match
                </Link>
              </Button>
              <Button size="sm" variant="outline" asChild data-testid="button-navigate-view-boards">
                <Link href="/board-opportunities">
                  <Award className="mr-1 h-3 w-3" />
                  View Boards
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="hover-elevate" data-testid="card-advisor-mentorship">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-3">
              <Calendar className="h-5 w-5 text-teal-600 dark:text-teal-400" />
              <h3 className="font-semibold text-foreground">Office Hours & Mentorship</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Host or join mentorship sessions and peer circles</p>
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" variant="outline" asChild data-testid="button-navigate-advisor-office-hours">
                <Link href="/network/office-hours">
                  <Calendar className="mr-1 h-3 w-3" />
                  Office Hours
                </Link>
              </Button>
              <Button size="sm" variant="outline" asChild data-testid="button-navigate-advisor-circles">
                <Link href="/circles">
                  <Users className="mr-1 h-3 w-3" />
                  Circles
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="hover-elevate" data-testid="card-advisor-network">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-3">
              <Users className="h-5 w-5 text-teal-600 dark:text-teal-400" />
              <h3 className="font-semibold text-foreground">Advisor Network</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Connect with other advisors and find collaboration opportunities</p>
            <Button size="sm" variant="outline" asChild data-testid="button-navigate-browse-advisors">
              <Link href="/directory?tab=advisors">
                <Users className="mr-1 h-3 w-3" />
                Browse Advisors
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
